import React, { useState, useEffect } from "react";
import "./DynamicDeposit.css";
import { IoCloseSharp } from "react-icons/io5";
import { BiCopy, BiCheck, BiChevronLeft } from "react-icons/bi";
import { RiArrowDownSLine } from "react-icons/ri";
import Cookies from "js-cookie";
import { sendNotfication, API } from 'utils/Helper';
import storage from "utils/Storage";
import { useCreateDynamicDepositMutation, useCreateDynamicWithdrawalMutation, useActiveGatewaysQuery, useCurrenciesQuery } from "hooks/usePayment";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function CoinIcon({ gateway, size = 32 }) {
  const colors = ["#f7931a", "#627eea", "#26a17b", "#ef0027", "#345d9d", "#1475e1", "#16a34a"];
  const colorIndex = gateway.currency.charCodeAt(0) % colors.length;
  if (gateway.logo_url) {
    return (
      <img
        src={`${API}${gateway.logo_url}`}
        alt={gateway.currency}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "contain" }}
      />
    );
  }
  return (
    <span
      className="dd-coin-fallback"
      style={{ width: size, height: size, background: colors[colorIndex], fontSize: size * 0.42 }}
    >
      {gateway.currency.charAt(0)}
    </span>
  );
}

// ─── Tab Switcher (Crypto / Local Currency) ───────────────────────────────────

function TabSwitch({ tab, setTab }) {
  return (
    <div className="dd-tab-switch">
      <button
        className={`dd-tab-btn ${tab === "crypto" ? "active" : ""}`}
        onClick={() => setTab("crypto")}
      >
        Crypto
      </button>
      <button
        className={`dd-tab-btn ${tab === "local" ? "active" : ""}`}
        onClick={() => setTab("local")}
      >
        Local Currency
      </button>
    </div>
  );
}

// ─── Currency Selection View ──────────────────────────────────────────────────

function CurrencySelectView({ mode, depositGateways, withdrawalGateways, onPickCurrency, currencyTypeMap, currencies }) {
  const [tab, setTab] = useState("crypto");
  const gateways = mode === "deposit" ? depositGateways : withdrawalGateways;

  // Flatten gateways so that a gateway with multiple currencies (e.g. "USDT,BTC") 
  // is treated as a separate entry for each currency.
  const flatGateways = gateways.flatMap(g => {
    if (!g.currency) return [];
    const codes = g.currency.split(',').map(c => c.trim().toUpperCase());
    return codes.map(code => ({
      ...g,
      currency: code
    }));
  });

  const getType = (code) => currencyTypeMap[code] || 'fiat';
  const cryptoGateways = flatGateways.filter(g => getType(g.currency) === "crypto");
  const localGateways = flatGateways.filter(g => getType(g.currency) !== "crypto");

  const list = tab === "crypto" ? cryptoGateways : localGateways;

  // Group gateways by currency
  const grouped = list.reduce((acc, g) => {
    const code = g.currency;
    if (!acc[code]) acc[code] = [];
    acc[code].push(g);
    return acc;
  }, {});

  const currenciesList = Object.keys(grouped).map(code => {
    const currencyInfo = currencies.find(c => c.code?.toUpperCase() === code) || {};
    return {
      currency: code,
      gateways: grouped[code],
      representative: grouped[code][0],
      iconUrl: currencyInfo.image_url
    };
  });

  return (
    <div className="dd-currency-select-view">
      <TabSwitch tab={tab} setTab={setTab} />
      {currenciesList.length === 0 ? (
        <div className="dd-empty-msg">
          No {tab === "crypto" ? "crypto" : "local currency"} options available for {mode}.
        </div>
      ) : (
        <div className="dd-currency-list">
          {currenciesList.map(c => (
            <button key={c.currency} className="dd-currency-item" onClick={() => onPickCurrency(c)}>
              {c.iconUrl ? (
                <img
                  src={c.iconUrl.startsWith('http') ? c.iconUrl : c.iconUrl.startsWith('/') ? `${API}${c.iconUrl}` : `${API}/uploads/currencies/${c.iconUrl}`}
                  alt={c.currency}
                  style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "contain" }}
                />
              ) : (
                <CoinIcon gateway={c.representative} size={32} />
              )}
              <div className="dd-currency-item-texts">
                <span className="dd-currency-code">{c.currency}</span>
              </div>
              <BiChevronLeft size={18} className="dd-currency-arrow" style={{ transform: "rotate(180deg)" }} color="#a0aeb5" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Gateway Selection View ───────────────────────────────────────────────────

function GatewaySelectView({ currencyObj, onPickGateway }) {
  const getGatewayLabel = (gateway) => {
    if (gateway.display_name) return gateway.display_name;
    const name = gateway.gateway_name || '';
    if (name.startsWith('manual')) {
      const parts = name.split('_');
      if (parts.length > 1) {
        return parts.slice(1).map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
      }
      return 'Manual';
    }
    return name.charAt(0).toUpperCase() + name.slice(1) || 'Gateway';
  };

  return (
    <div className="dd-gateway-select-view">
      <div className="dd-currency-list">
        {currencyObj.gateways.map(g => (
          <button key={g.id} className="dd-currency-item" onClick={() => onPickGateway(g)}>
            <CoinIcon gateway={g} size={32} />
            <div className="dd-currency-item-texts">
              <span className="dd-currency-code">{getGatewayLabel(g)}</span>
              <span className="dd-currency-name">Gateway</span>
            </div>
            <BiChevronLeft size={18} className="dd-currency-arrow" style={{ transform: "rotate(180deg)" }} color="#a0aeb5" />
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Deposit Form Views ───────────────────────────────────────────────────────

const parseExtraConfig = (extraConfig) => {
  if (!extraConfig) return {};
  if (typeof extraConfig === 'object') return extraConfig;
  try {
    return JSON.parse(extraConfig);
  } catch {
    return {};
  }
};

function CryptoDepositView({ gateway, onSubmit, isLoading }) {
  const [copied, setCopied] = useState(false);
  const [copiedText, setCopiedText] = useState("");
  const [utrNumber, setUtrNumber] = useState("");
  const [amount, setAmount] = useState("");

  const gatewayNameLower = (gateway.gateway_name || "").toLowerCase();
  const isAutomatic = gatewayNameLower === "ccpayment" || gatewayNameLower === "nowpayments";
  const isManual = !isAutomatic;
  const isBank = gatewayNameLower === "bank_transfer";
  const extraConfig = parseExtraConfig(gateway.extra_config);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).catch(() => { });
    setCopied(true);
    setCopiedText(label);
    setTimeout(() => {
      setCopied(false);
      setCopiedText("");
    }, 2000);
  };

  const canSubmit = amount && (!isManual || utrNumber);

  return (
    <div className="dd-form-view">
      {/* Coin header row */}
      <div className="dd-selected-coin-row">
        <CoinIcon gateway={gateway} size={36} />
        <div>
          <div className="dd-sel-code">{gateway.currency.toUpperCase()}</div>
          <div className="dd-sel-name">{gateway.display_name || gateway.currency.toUpperCase()}</div>
        </div>
      </div>

      {/* QR + Address */}
      {isManual && gateway.qr_code_url && (
        <div className="dd-qr-row">
          <div className="dd-qr-img-wrap">
            <img src={`${API}${gateway.qr_code_url}`} alt="QR Code" />
          </div>
          <div className="dd-qr-details">
            <div className="dd-qr-label">Payment QR Code</div>
            <div className="dd-qr-addr">Scan QR to make payment</div>
            {gateway.instructions && (
              <div className="dd-qr-instructions" dangerouslySetInnerHTML={{ __html: gateway.instructions }} />
            )}
          </div>
        </div>
      )}

      {/* Instructions for bank transfer when no QR */}
      {isManual && !gateway.qr_code_url && gateway.instructions && (
        <div className="dd-bank-instructions" dangerouslySetInnerHTML={{ __html: gateway.instructions }} />
      )}

      {/* Payment Address / UPI ID */}
      {isManual && !isBank && (extraConfig.payment_address || gateway.wallet_address) && (
        <div className="dd-field-group">
          <label>Payment Address / UPI ID</label>
          <div className="dd-input-box">
            <input
              type="text"
              readOnly
              value={extraConfig.payment_address || gateway.wallet_address}
              style={{ paddingRight: "80px" }}
            />
            <button
              className="dd-detail-copy"
              style={{ position: "absolute", right: "12px", height: "30px" }}
              onClick={() => copyToClipboard(extraConfig.payment_address || gateway.wallet_address, "Address")}
            >
              {copied && copiedText === "Address" ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>
      )}

      {/* Bank Transfer Details */}
      {isManual && isBank && (
        <div className="dd-bank-details-box">
          <div className="dd-bank-detail-item">
            <span className="dd-detail-label">Bank Name:</span>
            <span className="dd-detail-value">{extraConfig.bank_name || ""}</span>
            <button className="dd-detail-copy" onClick={() => copyToClipboard(extraConfig.bank_name || "", "Bank")}>
              {copied && copiedText === "Bank" ? "Copied!" : "Copy"}
            </button>
          </div>
          <div className="dd-bank-detail-item">
            <span className="dd-detail-label">Account Name:</span>
            <span className="dd-detail-value">{extraConfig.account_holder || ""}</span>
            <button className="dd-detail-copy" onClick={() => copyToClipboard(extraConfig.account_holder || "", "Holder")}>
              {copied && copiedText === "Holder" ? "Copied!" : "Copy"}
            </button>
          </div>
          <div className="dd-bank-detail-item">
            <span className="dd-detail-label">Account No:</span>
            <span className="dd-detail-value">{extraConfig.account_number || ""}</span>
            <button className="dd-detail-copy" onClick={() => copyToClipboard(extraConfig.account_number || "", "Account")}>
              {copied && copiedText === "Account" ? "Copied!" : "Copy"}
            </button>
          </div>
          <div className="dd-bank-detail-item">
            <span className="dd-detail-label">IFSC Code:</span>
            <span className="dd-detail-value">{extraConfig.ifsc || ""}</span>
            <button className="dd-detail-copy" onClick={() => copyToClipboard(extraConfig.ifsc || "", "IFSC")}>
              {copied && copiedText === "IFSC" ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>
      )}

      {/* Amount */}
      <div className="dd-field-group">
        <label>Amount ({gatewayNameLower === "nowpayments" ? "USD" : gateway.currency.toUpperCase()})</label>
        <div className="dd-input-box">
          <input
            type="number"
            placeholder={`Min: ${gateway.min_amount} – Max: ${gateway.max_amount || "∞"}`}
            value={amount}
            onChange={e => setAmount(e.target.value)}
          />
          <span className="dd-input-suffix">{gatewayNameLower === "nowpayments" ? "USD" : gateway.currency.toUpperCase()}</span>
        </div>
      </div>

      {/* Transaction ID / UTR */}
      {isManual && (
        <div className="dd-field-group">
          <label>Transaction ID / UTR</label>
          <div className="dd-input-box">
            <input
              type="text"
              placeholder="Enter Transaction ID / UTR Number"
              value={utrNumber}
              onChange={e => setUtrNumber(e.target.value)}
            />
          </div>
        </div>
      )}

      <button
        className="dd-action-btn"
        onClick={() => onSubmit({ amount, utrNumber, gateway })}
        disabled={isLoading || !canSubmit}
      >
        {isLoading ? "Processing…" : `Deposit ${gatewayNameLower === "nowpayments" ? "USD" : gateway.currency.toUpperCase()}`}
      </button>
    </div>
  );
}

function LocalDepositView({ gateway, onSubmit, isLoading }) {
  const [copied, setCopied] = useState(false);
  const [copiedText, setCopiedText] = useState("");
  const [amount, setAmount] = useState("");
  const [utrNumber, setUtrNumber] = useState("");

  const gatewayNameLower = (gateway.gateway_name || "").toLowerCase();
  const isAutomatic = gatewayNameLower === "ccpayment" || gatewayNameLower === "nowpayments";
  const isManual = !isAutomatic;
  const isBank = gatewayNameLower === "bank_transfer";
  const extraConfig = parseExtraConfig(gateway.extra_config);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).catch(() => { });
    setCopied(true);
    setCopiedText(label);
    setTimeout(() => {
      setCopied(false);
      setCopiedText("");
    }, 2000);
  };

  const canSubmit = amount && (!isManual || utrNumber);

  return (
    <div className="dd-form-view">
      {/* Coin header row */}
      <div className="dd-selected-coin-row">
        <CoinIcon gateway={gateway} size={36} />
        <div>
          <div className="dd-sel-code">{gateway.currency.toUpperCase()}</div>
          <div className="dd-sel-name">{gateway.display_name || gateway.currency.toUpperCase()}</div>
        </div>
      </div>

      {/* QR */}
      {isManual && gateway.qr_code_url && (
        <div className="dd-qr-row">
          <div className="dd-qr-img-wrap">
            <img src={`${API}${gateway.qr_code_url}`} alt="QR Code" />
          </div>
          <div className="dd-qr-details">
            <div className="dd-qr-label">Payment QR</div>
            <div className="dd-qr-addr">Scan to pay via UPI / local method</div>
            {gateway.instructions && (
              <div className="dd-qr-instructions" dangerouslySetInnerHTML={{ __html: gateway.instructions }} />
            )}
          </div>
        </div>
      )}

      {/* Instructions for bank transfer when no QR */}
      {isManual && !gateway.qr_code_url && gateway.instructions && (
        <div className="dd-bank-instructions" dangerouslySetInnerHTML={{ __html: gateway.instructions }} />
      )}

      {/* Payment Address / UPI ID */}
      {isManual && !isBank && (extraConfig.payment_address || gateway.wallet_address) && (
        <div className="dd-field-group">
          <label>Payment Address / UPI ID</label>
          <div className="dd-input-box">
            <input
              type="text"
              readOnly
              value={extraConfig.payment_address || gateway.wallet_address}
              style={{ paddingRight: "80px" }}
            />
            <button
              className="dd-detail-copy"
              style={{ position: "absolute", right: "12px", height: "30px" }}
              onClick={() => copyToClipboard(extraConfig.payment_address || gateway.wallet_address, "Address")}
            >
              {copied && copiedText === "Address" ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>
      )}

      {/* Bank Transfer Details */}
      {isManual && isBank && (
        <div className="dd-bank-details-box">
          <div className="dd-bank-detail-item">
            <span className="dd-detail-label">Bank Name:</span>
            <span className="dd-detail-value">{extraConfig.bank_name || ""}</span>
            <button className="dd-detail-copy" onClick={() => copyToClipboard(extraConfig.bank_name || "", "Bank")}>
              {copied && copiedText === "Bank" ? "Copied!" : "Copy"}
            </button>
          </div>
          <div className="dd-bank-detail-item">
            <span className="dd-detail-label">Account Name:</span>
            <span className="dd-detail-value">{extraConfig.account_holder || ""}</span>
            <button className="dd-detail-copy" onClick={() => copyToClipboard(extraConfig.account_holder || "", "Holder")}>
              {copied && copiedText === "Holder" ? "Copied!" : "Copy"}
            </button>
          </div>
          <div className="dd-bank-detail-item">
            <span className="dd-detail-label">Account No:</span>
            <span className="dd-detail-value">{extraConfig.account_number || ""}</span>
            <button className="dd-detail-copy" onClick={() => copyToClipboard(extraConfig.account_number || "", "Account")}>
              {copied && copiedText === "Account" ? "Copied!" : "Copy"}
            </button>
          </div>
          <div className="dd-bank-detail-item">
            <span className="dd-detail-label">IFSC Code:</span>
            <span className="dd-detail-value">{extraConfig.ifsc || ""}</span>
            <button className="dd-detail-copy" onClick={() => copyToClipboard(extraConfig.ifsc || "", "IFSC")}>
              {copied && copiedText === "IFSC" ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>
      )}

      {/* Amount */}
      <div className="dd-field-group">
        <label>Amount ({gatewayNameLower === "nowpayments" ? "USD" : gateway.currency.toUpperCase()})</label>
        <div className="dd-input-box">
          <input
            type="number"
            placeholder={`Min: ${gateway.min_amount} – Max: ${gateway.max_amount || "∞"}`}
            value={amount}
            onChange={e => setAmount(e.target.value)}
          />
          <span className="dd-input-suffix">{gatewayNameLower === "nowpayments" ? "USD" : gateway.currency.toUpperCase()}</span>
        </div>
        {gateway.min_amount && (
          <p className="dd-limit-hint">Minimum: {gatewayNameLower === "nowpayments" ? "USD" : gateway.currency.toUpperCase()} {gateway.min_amount}{gateway.max_amount ? ` | Maximum: ${gatewayNameLower === "nowpayments" ? "USD" : gateway.currency.toUpperCase()} ${gateway.max_amount}` : ""}</p>
        )}
      </div>

      {/* UTR */}
      {isManual && (
        <div className="dd-field-group">
          <label>Transaction ID / UTR</label>
          <div className="dd-input-box">
            <input
              type="text"
              placeholder="Enter Transaction ID / UTR Number"
              value={utrNumber}
              onChange={e => setUtrNumber(e.target.value)}
            />
          </div>
        </div>
      )}

      <button
        className="dd-action-btn"
        onClick={() => onSubmit({ amount, utrNumber, gateway })}
        disabled={isLoading || !canSubmit}
      >
        {isLoading ? "Processing…" : `Deposit ${gatewayNameLower === "nowpayments" ? "USD" : gateway.currency.toUpperCase()}`}
      </button>
    </div>
  );
}

// ─── Withdrawal Form Views ────────────────────────────────────────────────────

function CryptoWithdrawView({ gateway, onSubmit, isLoading }) {
  const [amount, setAmount] = useState("");
  const [address, setAddress] = useState("");

  const canSubmit = amount && address;

  return (
    <div className="dd-form-view">
      <div className="dd-selected-coin-row">
        <CoinIcon gateway={gateway} size={36} />
        <div>
          <div className="dd-sel-code">{gateway.currency}</div>
          <div className="dd-sel-name">{gateway.display_name || gateway.currency}</div>
        </div>
      </div>

      <div className="dd-field-group">
        <label>Withdraw Address</label>
        <div className="dd-input-box">
          <input
            type="text"
            placeholder={`Enter ${gateway.currency} address`}
            value={address}
            onChange={e => setAddress(e.target.value)}
          />
        </div>
      </div>

      <div className="dd-field-group">
        <label>Amount ({gateway.currency})</label>
        <div className="dd-input-box">
          <input
            type="number"
            placeholder={`Min: ${gateway.min_amount} – Max: ${gateway.max_amount || "∞"}`}
            value={amount}
            onChange={e => setAmount(e.target.value)}
          />
          <span className="dd-input-suffix">{gateway.currency}</span>
        </div>
        {gateway.min_amount && (
          <p className="dd-limit-hint">Minimum: {gateway.currency} {gateway.min_amount}{gateway.max_amount ? ` | Maximum: ${gateway.currency} ${gateway.max_amount}` : ""}</p>
        )}
      </div>

      <button
        className="dd-action-btn"
        onClick={() => onSubmit({ amount, address, gateway })}
        disabled={isLoading || !canSubmit}
      >
        {isLoading ? "Processing…" : `Withdraw ${gateway.currency}`}
      </button>

      {gateway.instructions && (
        <p className="dd-help-text" dangerouslySetInnerHTML={{ __html: gateway.instructions }} />
      )}
    </div>
  );
}

function LocalWithdrawView({ gateway, onSubmit, isLoading }) {
  const [amount, setAmount] = useState("");
  const [address, setAddress] = useState("");
  const presets = [1000, 5000, 10000];

  const canSubmit = amount && address;

  return (
    <div className="dd-form-view">
      <div className="dd-selected-coin-row">
        <CoinIcon gateway={gateway} size={36} />
        <div>
          <div className="dd-sel-code">{gateway.currency}</div>
          <div className="dd-sel-name">{gateway.display_name || gateway.currency}</div>
        </div>
      </div>

      {/* Preset amounts */}
      <div className="dd-presets">
        {presets.map(p => (
          <button
            key={p}
            className={`dd-preset-btn ${amount === String(p) ? "active" : ""}`}
            onClick={() => setAmount(String(p))}
          >
            {p.toLocaleString()}
          </button>
        ))}
      </div>

      <div className="dd-field-group">
        <label>Amount ({gateway.currency})</label>
        <div className="dd-input-box">
          <input
            type="number"
            placeholder={`Min: ${gateway.min_amount} – Max: ${gateway.max_amount || "∞"}`}
            value={amount}
            onChange={e => setAmount(e.target.value)}
          />
          <span className="dd-input-suffix">{gateway.currency}</span>
        </div>
        {gateway.min_amount && (
          <p className="dd-limit-hint">Minimum: {gateway.currency} {gateway.min_amount}{gateway.max_amount ? ` | Maximum: ${gateway.currency} ${gateway.max_amount}` : ""}</p>
        )}
      </div>

      <div className="dd-field-group">
        <label>Account / UPI / Wallet Address</label>
        <div className="dd-input-box">
          <input
            type="text"
            placeholder="Enter your account or UPI ID"
            value={address}
            onChange={e => setAddress(e.target.value)}
          />
        </div>
      </div>

      <button
        className="dd-action-btn"
        onClick={() => onSubmit({ amount, address, gateway })}
        disabled={isLoading || !canSubmit}
      >
        {isLoading ? "Processing…" : `Withdraw ${gateway.currency}`}
      </button>

      {gateway.instructions && (
        <p className="dd-help-text" dangerouslySetInnerHTML={{ __html: gateway.instructions }} />
      )}
    </div>
  );
}

// ─── Payment Details View (For Automatic Gateways returning address/amount) ───

function PaymentDetailsView({ paymentDetails, onClose }) {
  const [copied, setCopied] = useState(false);
  const [copiedText, setCopiedText] = useState("");

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).catch(() => { });
    setCopied(true);
    setCopiedText(label);
    setTimeout(() => {
      setCopied(false);
      setCopiedText("");
    }, 2000);
  };

  const payAddress = paymentDetails?.pay_address || "";
  const payAmount = paymentDetails?.pay_amount || "";
  const payCurrency = paymentDetails?.pay_currency || "";
  const paymentId = paymentDetails?.payment_id || "";

  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(payAddress)}`;

  return (
    <div className="dd-form-view">
      <div style={{ textAlign: "center", marginBottom: "10px" }}>
        <h3 style={{ color: "#fff", fontSize: "16px", margin: "0 0 5px 0" }}>Direct Payment Details</h3>
        <p style={{ color: "#a0aeb5", fontSize: "12px", margin: 0 }}>Scan the QR code or copy details to pay</p>
      </div>

      <div style={{ display: "flex", justifyContent: "center", background: "#0f212e", padding: "20px", borderRadius: "10px" }}>
        <div style={{ background: "#fff", padding: "10px", borderRadius: "8px" }}>
          <img src={qrCodeUrl} alt="QR Code" style={{ width: "150px", height: "150px", display: "block" }} />
        </div>
      </div>

      <div className="dd-field-group">
        <label>Amount to Send</label>
        <div className="dd-input-box">
          <input
            type="text"
            readOnly
            value={`${payAmount} ${payCurrency.toUpperCase()}`}
            style={{ paddingRight: "80px", fontFamily: "monospace", fontWeight: "bold" }}
          />
          <button
            className="dd-detail-copy"
            style={{ position: "absolute", right: "12px", height: "30px" }}
            onClick={() => copyToClipboard(String(payAmount), "Amount")}
          >
            {copied && copiedText === "Amount" ? "Copied!" : "Copy"}
          </button>
        </div>
      </div>

      <div className="dd-field-group">
        <label>Deposit Address</label>
        <div className="dd-input-box">
          <input
            type="text"
            readOnly
            value={payAddress}
            style={{ paddingRight: "80px", fontFamily: "monospace" }}
          />
          <button
            className="dd-detail-copy"
            style={{ position: "absolute", right: "12px", height: "30px" }}
            onClick={() => copyToClipboard(payAddress, "Address")}
          >
            {copied && copiedText === "Address" ? "Copied!" : "Copy"}
          </button>
        </div>
      </div>

      {paymentId && (
        <div style={{ fontSize: "12px", color: "#a0aeb5", display: "flex", justifyContent: "space-between", padding: "0 4px" }}>
          <span>Payment ID:</span>
          <span style={{ fontFamily: "monospace", color: "#fff" }}>{paymentId}</span>
        </div>
      )}

      <div style={{
        fontSize: "11px",
        color: "#a0aeb5",
        background: "rgba(20, 117, 225, 0.08)",
        borderLeft: "2px solid #1475e1",
        padding: "10px",
        borderRadius: "0 6px 6px 0",
        lineHeight: "1.5"
      }}>
        <strong>Crucial Instruction:</strong> Only send <strong>{payCurrency.toUpperCase()}</strong> to this address. Sending any other coin will result in permanent loss. Your deposit will be credited automatically after blockchain confirmation.
      </div>

      <button className="dd-action-btn" onClick={onClose}>
        Done
      </button>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

function DynamicDeposit({ style, children, forceOpen, onForceClose, defaultTab = "Deposit" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("Deposit"); // "Deposit" | "Withdraw"
  const [step, setStep] = useState("currency-select"); // "currency-select" | "gateway-select" | "deposit-form" | "withdraw-form" | "payment-details"
  const [selectedCurrencyObj, setSelectedCurrencyObj] = useState(null);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);

  const { data: gateways = [], isLoading } = useActiveGatewaysQuery(isOpen);
  const { data: currencies = [] } = useCurrenciesQuery();
  const depositMutation = useCreateDynamicDepositMutation();
  const withdrawMutation = useCreateDynamicWithdrawalMutation();

  // Build a map: { "USDT": "crypto", "INR": "fiat", ... } from the currencies table
  const currencyTypeMap = currencies.reduce((map, c) => {
    map[c.code?.toUpperCase()] = c.currency_type || 'fiat';
    return map;
  }, {});

  // Also tag each gateway with its resolved type (for use in form views)
  const resolveType = (g) => currencyTypeMap[g.currency?.toUpperCase()] || 'fiat';

  const depositGateways = gateways.filter(g => g.type === "deposit");
  const withdrawalGateways = gateways.filter(g => g.type === "withdrawal");

  useEffect(() => {
    if (forceOpen) {
      setIsOpen(true);
      setActiveTab(defaultTab);
      setStep("currency-select");
      setSelectedCurrencyObj(null);
      setSelectedGateway(null);
      setPaymentDetails(null);
    } else {
      setIsOpen(false);
    }
  }, [forceOpen, defaultTab]);

  const handleClose = () => {
    setIsOpen(false);
    setStep("currency-select");
    setSelectedCurrencyObj(null);
    setSelectedGateway(null);
    setPaymentDetails(null);
    if (onForceClose) onForceClose();
  };

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    setStep("currency-select");
    setSelectedCurrencyObj(null);
    setSelectedGateway(null);
    setPaymentDetails(null);
  };

  const handlePickCurrency = (currencyObj) => {
    setSelectedCurrencyObj(currencyObj);
    setStep("gateway-select");
  };

  const handlePickGateway = (gateway) => {
    setSelectedGateway(gateway);
    setStep(activeTab === "Deposit" ? "deposit-form" : "withdraw-form");
  };

  const handleBack = () => {
    if (step === "payment-details") {
      setStep("deposit-form");
      setPaymentDetails(null);
    } else if (step === "deposit-form" || step === "withdraw-form") {
      setStep("gateway-select");
      setSelectedGateway(null);
    } else if (step === "gateway-select") {
      setStep("currency-select");
      setSelectedCurrencyObj(null);
    } else {
      setStep("currency-select");
      setSelectedCurrencyObj(null);
      setSelectedGateway(null);
      setPaymentDetails(null);
    }
  };

  // ── Step title for header ──
  const stepTitle = step === "currency-select"
    ? activeTab
    : step === "gateway-select"
      ? `Select Gateway (${selectedCurrencyObj?.currency || ""})`
      : step === "payment-details"
        ? "Send Payment"
        : `${activeTab} ${selectedGateway?.currency || ""}`;

  const getUidSafe = () => {
    let rawUid = Cookies.get("uid") || Cookies.get("UID");
    if (rawUid) {
      const clean = String(rawUid).replace(/['"]+/g, '').trim();
      const parsed = parseFloat(clean);
      if (!isNaN(parsed)) {
        // Sync to localStorage for robustness
        localStorage.setItem("uid", String(parsed));
        return parsed;
      }
    }
    const localUid = localStorage.getItem("uid") || localStorage.getItem("user_id");
    if (localUid) {
      const clean = String(localUid).replace(/['"]+/g, '').trim();
      const parsed = parseFloat(clean);
      if (!isNaN(parsed)) return parsed;
    }
    // Fallback: Decode JWT Token
    try {
      const token = storage.getKey('token') || Cookies.get('session');
      if (token) {
        const base64Url = token.split('.')[1];
        if (base64Url) {
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(
            atob(base64)
              .split('')
              .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
              .join('')
          );
          const parsed = JSON.parse(jsonPayload);
          const val = parsed.id || parsed.uid || parsed.sub;
          if (val) {
            const parsedVal = parseFloat(String(val).replace(/['"]+/g, '').trim());
            if (!isNaN(parsedVal)) {
              // Sync back to cookie and localStorage
              Cookies.set("uid", String(parsedVal), { expires: 14, path: '/' });
              localStorage.setItem("uid", String(parsedVal));
              return parsedVal;
            }
          }
        }
      }
    } catch (e) {
      console.error("JWT decoding failed in DynamicDeposit:", e);
    }
    return null;
  };

  // ── Deposit submit ──
  const handleDepositSubmit = ({ amount, utrNumber, gateway }) => {
    const userUid = getUidSafe();
    if (!userUid) {
      sendNotfication("User session not found. Please log in again.", "danger", "bottom-left");
      return;
    }
    const payload = {
      uid: userUid,
      money: parseFloat(amount),
      gateway_id: gateway.id,
      extra_fields: { utrNumber: utrNumber ? utrNumber.trim() : "", currency: selectedCurrencyObj?.currency || gateway.currency }
    };
    depositMutation.mutate(payload, {
      onSuccess: (data) => {
        if (data.success) {
          if (data.data?.checkoutUrl) {
            sendNotfication(data.message || "Deposit request submitted!", "success", "bottom-left");
            window.location.href = data.data.checkoutUrl;
          } else if (data.data?.pay_address) {
            setPaymentDetails(data.data);
            setStep("payment-details");
          } else {
            sendNotfication(data.message || "Deposit request submitted!", "success", "bottom-left");
            handleClose();
          }
        } else {
          sendNotfication(data.message || "Failed", "danger", "bottom-left");
        }
      },
      onError: (error) => sendNotfication(error.message || "An error occurred", "danger", "bottom-left"),
    });
  };

  // ── Withdrawal submit ──
  const handleWithdrawSubmit = ({ amount, address, gateway }) => {
    const userUid = getUidSafe();
    if (!userUid) {
      sendNotfication("User session not found. Please log in again.", "danger", "bottom-left");
      return;
    }
    const payload = {
      uid: userUid,
      money: parseFloat(amount),
      gateway_id: gateway.id,
      extra_fields: { address, currency: selectedCurrencyObj?.currency || gateway.currency }
    };
    withdrawMutation.mutate(payload, {
      onSuccess: (data) => {
        if (data.success) {
          sendNotfication(data.message || "Withdrawal request submitted!", "success", "bottom-left");
          handleClose();
        } else {
          sendNotfication(data.message || "Failed", "danger", "bottom-left");
        }
      },
      onError: (error) => sendNotfication(error.message || "An error occurred", "danger", "bottom-left"),
    });
  };

  return (
    <div>
      <button style={style} onClick={() => { setIsOpen(true); setActiveTab(defaultTab); setStep("currency-select"); setSelectedGateway(null); setPaymentDetails(null); }}>
        {children}
      </button>

      {isOpen && (
        <div className="dd-overlay" onClick={handleClose}>
          <div className="dd-modal" onClick={e => e.stopPropagation()}>

            {/* ── Header ── */}
            <div className="dd-header">
              <div className="dd-header-left">
                <button className="dd-back-btn" onClick={step !== "currency-select" ? handleBack : handleClose}>
                  <BiChevronLeft size={20} />
                  <span>Wallet</span>
                </button>
                <span className="dd-slash">/</span>
                <span className="dd-header-title">{stepTitle}</span>
              </div>
              <button className="dd-close-btn" onClick={handleClose}>
                <IoCloseSharp size={22} />
              </button>
            </div>

            {/* ── Scrollable Content ── */}
            <div className="dd-content custom-scrollbar">
              {isLoading ? (
                <div className="dd-loading">Loading…</div>
              ) : step === "currency-select" ? (
                <CurrencySelectView
                  mode={activeTab === "Deposit" ? "deposit" : "withdraw"}
                  currencyTypeMap={currencyTypeMap}
                  currencies={currencies}
                  depositGateways={depositGateways}
                  withdrawalGateways={withdrawalGateways}
                  onPickCurrency={handlePickCurrency}
                />
              ) : step === "gateway-select" && selectedCurrencyObj ? (
                <GatewaySelectView
                  currencyObj={selectedCurrencyObj}
                  onPickGateway={handlePickGateway}
                />
              ) : step === "deposit-form" && selectedGateway ? (
                resolveType(selectedGateway) === "crypto" ? (
                  <CryptoDepositView
                    gateway={selectedGateway}
                    onSubmit={handleDepositSubmit}
                    isLoading={depositMutation.isLoading}
                  />
                ) : (
                  <LocalDepositView
                    gateway={selectedGateway}
                    onSubmit={handleDepositSubmit}
                    isLoading={depositMutation.isLoading}
                  />
                )
              ) : step === "withdraw-form" && selectedGateway ? (
                resolveType(selectedGateway) === "crypto" ? (
                  <CryptoWithdrawView
                    gateway={selectedGateway}
                    onSubmit={handleWithdrawSubmit}
                    isLoading={withdrawMutation.isLoading}
                  />
                ) : (
                  <LocalWithdrawView
                    gateway={selectedGateway}
                    onSubmit={handleWithdrawSubmit}
                    isLoading={withdrawMutation.isLoading}
                  />
                )
              ) : step === "payment-details" && paymentDetails ? (
                <PaymentDetailsView
                  paymentDetails={paymentDetails}
                  onClose={handleClose}
                />
              ) : null}
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

export default DynamicDeposit;
